import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import { contractReferenceLabel } from '../lib/contractColumns'
import { ConfigVersionHistoryButton } from '../components/ConfigVersionHistory'
import {
  fetchPipelines,
  fetchPipeline,
  fetchLeadsForPipeline,
  fetchContractsForPipeline,
  moveLeadToStage,
  moveContractToStage,
  setInitialStage,
  deactivatePipeline,
  type ConfigurationUsageRow,
  type Pipeline,
  type PipelineDetail,
  type PipelineStage,
  type LeadKanbanCard,
  type ContractKanbanCard,
} from '../api/pipelines'
import { api } from '../api/client'
import { CloneConfigurationButton } from '../components/CloneConfigurationButton'
import { UsageConfirmDialog } from '../components/UsageConfirmDialog'
import { departmentsApi, type Department } from '../api/team'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import {
  Button,
  Callout,
  Card,
  Chip,
  EmptyState,
  FilterBar,
  Icon,
  IconButton,
  LoadingState,
  Page,
  PageHeader,
  Radio,
  SegmentedButton,
  Select,
  StatusPill,
  TextField,
} from '../components/ui'
import './PipelinesPage.css'

/** Minimal shape of `GET /api/operators`'s items — mirrors ContestsPage.tsx's own
 * `OperatorOption` (there is no shared `operatorsApi` client in this repo to import instead; that
 * page's inline `api<{ items: OperatorOption[] }>('/api/operators?size=200')` call is the
 * established pattern this reuses, not a duplicate of it). */
interface OperatorOption {
  id: number
  name: string
}

/** One card as the board renders it, entity-agnostic — built from either a LeadKanbanCard or a
 * ContractKanbanCard so KanbanColumn/KanbanCard don't need to know which entity type is active.
 *
 * TASK-0067: createdAt/departmentId/agentId/operatorId added so the Kanban "Filtri" panel can
 * filter client-side over the already-loaded board (see api/pipelines.ts's remarks on
 * fetchLeadsForPipeline/fetchContractsForPipeline for why the whole pipeline is fetched in one
 * call already — client-side filtering reuses that same loaded set instead of adding new
 * server-side query params/round-trips). operatorId is always null for a lead card — Lead has no
 * analogous field (verified by reading Lead.cs; see leadToBoardCard below). */
interface BoardCard {
  id: number
  stageId: number | null
  title: string
  subtitle: string | null
  amount: number
  statusLabel: string
  createdAt: string
  departmentId: number | null
  agentId: number | null
  operatorId: number | null
  /** Pagina di dettaglio aperta al clic sulla card. */
  href: string
}

function leadToBoardCard(l: LeadKanbanCard): BoardCard {
  const name = [l.firstName, l.lastName].filter(Boolean).join(' ').trim()
  return {
    id: l.id,
    stageId: l.stageId,
    title: name || l.companyName || `Lead #${l.id}`,
    subtitle: l.companyName && name ? l.companyName : l.email,
    amount: l.estimatedValue ?? 0,
    statusLabel: l.status,
    createdAt: l.createdAt,
    departmentId: l.departmentId,
    agentId: l.assignedAgentId,
    operatorId: null,
    href: `/leads/${l.id}`,
  }
}

function contractToBoardCard(c: ContractKanbanCard): BoardCard {
  return {
    id: c.id,
    stageId: c.stageId,
    title: contractReferenceLabel({ id: c.id, referenceCode: c.referenceCode, date: c.createdAt?.slice(0, 10) }),
    subtitle: c.customerName ?? null,
    amount: c.expectedAmount ?? c.baseAmount ?? 0,
    statusLabel: c.status,
    createdAt: c.createdAt,
    departmentId: c.departmentId,
    agentId: c.agentId,
    operatorId: c.operatorId,
    href: `/contracts/${c.id}`,
  }
}

/** TASK-0067: applied filter values (a snapshot of the Filtri panel's controls taken when
 * "Filtra" is clicked — see handleFilterApply). Kept separate from the live dateFrom/dateTo/
 * department/agent/operator state so editing a control doesn't narrow the board until the user
 * actually clicks "Filtra" (matching the existing panel's own Filtra/Reset two-button design). */
interface KanbanFilters {
  dateFrom: string
  dateTo: string
  department: string
  agent: string
  operator: string
}

const EMPTY_FILTERS: KanbanFilters = { dateFrom: '', dateTo: '', department: '', agent: '', operator: '' }

/**
 * TASK-0067: whether `card` matches `filters`. Mirrors ContractsController.List's own dateFrom/
 * dateTo semantics (inclusive lower bound, EXCLUSIVE day-after upper bound) so a client-side
 * "Dal 01/08 Al 05/08" reads the same way a server-side one would — see that controller's remarks
 * on why dateTo is exclusive-next-day rather than a bare `<=` compare.
 */
function cardMatchesFilters(card: BoardCard, filters: KanbanFilters): boolean {
  const createdAt = new Date(card.createdAt)
  if (filters.dateFrom) {
    const from = new Date(`${filters.dateFrom}T00:00:00Z`)
    if (createdAt < from) return false
  }
  if (filters.dateTo) {
    const toExclusive = new Date(`${filters.dateTo}T00:00:00Z`)
    toExclusive.setUTCDate(toExclusive.getUTCDate() + 1)
    if (createdAt >= toExclusive) return false
  }
  if (filters.department && String(card.departmentId ?? '') !== filters.department) return false
  if (filters.agent && String(card.agentId ?? '') !== filters.agent) return false
  if (filters.operator && String(card.operatorId ?? '') !== filters.operator) return false
  return true
}

function formatCurrency(value: number): string {
  return `€ ${value.toLocaleString('it-IT', { maximumFractionDigits: 0 })}`
}

export function PipelinesPage() {
  const [entityType, setEntityType] = useState<'contract' | 'lead'>('contract')
  const [pipelines, setPipelines] = useState<Pipeline[]>([])
  const [selectedPipeline, setSelectedPipeline] = useState<number | null>(null)
  const [pipelineDetail, setPipelineDetail] = useState<PipelineDetail | null>(null)
  // F-142 (REQ-293-001): disattivazione del flusso con elenco degli usi e conferma.
  const [usageDialog, setUsageDialog] = useState<ConfigurationUsageRow[] | null>(null)
  const [deactivating, setDeactivating] = useState(false)
  const [deactivateError, setDeactivateError] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [showStageSettings, setShowStageSettings] = useState(false)
  const [savingStageId, setSavingStageId] = useState<number | null>(null)
  const [stageError, setStageError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // TASK-0041: real Kanban data — every card of the selected pipeline, entity-agnostic once
  // mapped to BoardCard. cardsLoading/movingId are separate from the page's own loading/error so a
  // card move doesn't flash the whole board back to the "Caricamento..." state.
  const [cards, setCards] = useState<BoardCard[]>([])
  const [cardsLoading, setCardsLoading] = useState(false)
  const [movingId, setMovingId] = useState<number | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)

  // Filter state — dateFrom/dateTo/department/agent/operator are the panel's live controls;
  // appliedFilters (TASK-0067) is the snapshot actually applied to the board, only updated when
  // "Filtra" is clicked (see handleFilterApply/cardMatchesFilters above).
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [department, setDepartment] = useState('')
  const [agent, setAgent] = useState('')
  const [operator, setOperator] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<KanbanFilters>(EMPTY_FILTERS)

  // TASK-0067: Reparto/Agente/Operatore select options — fetched once on mount, same
  // fetch-once/silently-swallow-errors pattern CompanyFormPage.tsx (agents, TASK-0059) and
  // FormsPage.tsx (departments) already use, reused rather than duplicated. "Agente" is filtered
  // server-side to role=EqualAgente exactly like CompanyFormPage's own agent selector — see its
  // remarks on why an unfiltered list would let this mean something it doesn't.
  const [departments, setDepartments] = useState<Department[]>([])
  const [agents, setAgents] = useState<DirectoryUser[]>([])
  const [operators, setOperators] = useState<OperatorOption[]>([])

  useEffect(() => {
    departmentsApi.list().then(setDepartments).catch(() => setDepartments([]))
    userDirectoryApi.list({ roles: ['EqualAgente'], activeOnly: false, limit: 200 }).then(setAgents).catch(() => setAgents([]))
    api<{ items: OperatorOption[] }>('/api/operators?size=200')
      .then(r => setOperators(r.items))
      .catch(() => setOperators([]))
  }, [])

  // TASK-0067: the Operatore control only renders for entityType === 'contract' (see the JSX
  // below and this task's report for why — Lead has no analogous field). If a value was picked
  // while on Contracts and the user then switches to Lead, clear it here rather than leaving a
  // stale, invisible filter silently zeroing out every lead card (every LeadKanbanCard.operatorId
  // is always null, so a leftover non-empty operator filter would otherwise match nothing).
  useEffect(() => {
    if (entityType === 'lead') {
      setOperator('')
      setAppliedFilters(f => (f.operator ? { ...f, operator: '' } : f))
    }
  }, [entityType])

  useEffect(() => {
    setLoading(true)
    fetchPipelines()
      .then(data => {
        setPipelines(data)
        const defaultPipeline = data.find(p => p.isDefault) ?? data[0] ?? null
        if (defaultPipeline) {
          setSelectedPipeline(defaultPipeline.id)
        } else {
          setLoading(false)
        }
      })
      .catch(e => {
        setError(e instanceof Error ? e.message : 'Errore nel caricamento')
        setLoading(false)
      })
  }, [])

  /**
   * F-142 (REQ-293-001): disattiva il flusso selezionato. Con la guardia accesa e usi bloccanti (lead e pratiche aperti)
   * il server risponde 409 con l'elenco: si apre la finestra e solo la conferma ripete la richiesta con `confirm=true`.
   */
  async function handleDeactivate(confirm: boolean) {
    if (!pipelineDetail) return
    setDeactivating(true)
    setDeactivateError(null)
    try {
      const res = await deactivatePipeline(pipelineDetail, confirm)
      if (res.status === 'needs-confirm') {
        setUsageDialog(res.usages)
        return
      }
      setUsageDialog(null)
      const remaining = pipelines.filter(p => p.id !== pipelineDetail.id)
      setPipelines(remaining)
      setSelectedPipeline(remaining.find(p => p.isDefault)?.id ?? remaining[0]?.id ?? null)
    } catch (e) {
      setUsageDialog(null)
      setDeactivateError(e instanceof Error ? e.message : 'Errore durante la disattivazione')
    } finally {
      setDeactivating(false)
    }
  }

  useEffect(() => {
    if (selectedPipeline === null) return
    setLoading(true)
    fetchPipeline(selectedPipeline)
      .then(data => setPipelineDetail(data))
      .catch(e => setError(e instanceof Error ? e.message : 'Errore nel caricamento pipeline'))
      .finally(() => setLoading(false))
  }, [selectedPipeline])

  // TASK-0041: real per-column counts/sums. Re-fetches whenever the pipeline or the
  // contract/lead toggle changes — one request for the whole board (see api/pipelines.ts's remarks
  // on fetchLeadsForPipeline/fetchContractsForPipeline for why this is a single call rather than
  // one per column).
  useEffect(() => {
    if (selectedPipeline === null) {
      setCards([])
      return
    }
    setCardsLoading(true)
    setMoveError(null)
    const request = entityType === 'lead'
      ? fetchLeadsForPipeline(selectedPipeline).then(items => items.map(leadToBoardCard))
      : fetchContractsForPipeline(selectedPipeline).then(items => items.map(contractToBoardCard))
    request
      .then(setCards)
      .catch(e => setError(e instanceof Error ? e.message : 'Errore nel caricamento delle card'))
      .finally(() => setCardsLoading(false))
  }, [selectedPipeline, entityType])

  // TASK-0067: the board renders filteredCards, not the raw cards fetched from the server —
  // client-side filtering over the already-loaded pipeline (see this task's report for why this
  // was chosen over adding new server-side query params: the board already loads every card of
  // the pipeline in one call, and the fields the panel filters on are already on BoardCard).
  const filteredCards = useMemo(
    () => cards.filter(card => cardMatchesFilters(card, appliedFilters)),
    [cards, appliedFilters],
  )

  const cardsByStage = useMemo(() => {
    const map = new Map<number, BoardCard[]>()
    for (const card of filteredCards) {
      if (card.stageId === null) continue
      const existing = map.get(card.stageId)
      if (existing) existing.push(card)
      else map.set(card.stageId, [card])
    }
    return map
  }, [filteredCards])

  const totalCount = filteredCards.length
  const totalValue = filteredCards.reduce((sum, c) => sum + c.amount, 0)

  /**
   * The Kanban's move action — drag a card onto another column's drop zone. Calls the new
   * PATCH /api/{leads|contracts}/{id}/pipeline (api/pipelines.ts's moveLeadToStage/
   * moveContractToStage), which invokes the previously-dead Lead.AssignPipeline/
   * Contract.SetPipeline domain methods.
   *
   * EXPLICIT NON-COUPLING: even when `targetStage.isClosedWon`/`isClosedLost` is true, this
   * handler only ever calls the pipeline-move endpoint — never a status-transition endpoint
   * (PATCH .../status). Lead.Status/Contract.Status stay separate, already-validated state
   * machines (TASK-0034/TASK-0025) that this task does not automate. A user who also wants to
   * close the lead/contract's status does so separately, with the existing status controls.
   *
   * Does NOT update local state optimistically and stop there — after the PATCH succeeds it
   * re-fetches the whole board from the server, so what's on screen is only ever what the server
   * actually persisted (the same data a reload would show), not a client-side guess.
   */
  async function handleMoveCard(card: BoardCard, targetStage: PipelineStage) {
    if (selectedPipeline === null || card.stageId === targetStage.id) return
    setMovingId(card.id)
    setMoveError(null)
    try {
      if (entityType === 'lead') {
        await moveLeadToStage(card.id, selectedPipeline, targetStage.id)
      } else {
        await moveContractToStage(card.id, selectedPipeline, targetStage.id)
      }
      const items = entityType === 'lead'
        ? (await fetchLeadsForPipeline(selectedPipeline)).map(leadToBoardCard)
        : (await fetchContractsForPipeline(selectedPipeline)).map(contractToBoardCard)
      setCards(items)
    } catch (e) {
      setMoveError(e instanceof Error ? e.message : 'Errore nello spostamento della card')
    } finally {
      setMovingId(null)
    }
  }

  /** TASK-0067: the "Filtra" button's handler — snapshots the panel's live controls into
   * appliedFilters, which is what actually narrows the board (see filteredCards above). Previously
   * this button had no onClick at all. */
  function handleFilterApply() {
    setAppliedFilters({ dateFrom, dateTo, department, agent, operator })
  }

  function handleFilterReset() {
    setDateFrom('')
    setDateTo('')
    setDepartment('')
    setAgent('')
    setOperator('')
    // TASK-0067: also clears the APPLIED filters, not just the controls — otherwise clicking
    // Reset would visually blank the panel while the board stayed narrowed to whatever was last
    // applied, until a future Filtra click "reset" it back. Reset must restore the full board.
    setAppliedFilters(EMPTY_FILTERS)
  }

  /** F-129: sceglie la fase iniziale; il server toglie il flag alle altre, qui si rilegge la pipeline. */
  async function handleSetInitialStage(stage: PipelineStage) {
    if (selectedPipeline === null || savingStageId !== null) return
    setSavingStageId(stage.id)
    setStageError(null)
    try {
      await setInitialStage(selectedPipeline, stage.id)
      setPipelineDetail(await fetchPipeline(selectedPipeline))
    } catch (e) {
      setStageError(e instanceof Error ? e.message : 'Impossibile impostare la fase iniziale')
    } finally {
      setSavingStageId(null)
    }
  }

  const sortedStages = pipelineDetail
    ? [...pipelineDetail.stages].sort((a, b) => a.sortOrder - b.sortOrder)
    : []

  return (
    <Page>
      <PageHeader
        title="Pipeline"
        icon="view_kanban"
        actions={
          <>
            {/* Filter toggle */}
            <Button
              type="button"
              variant="outlined"
              icon="tune"
              aria-expanded={showFilters}
              onClick={() => setShowFilters(v => !v)}
            >
              Filtri
            </Button>

            {/* Gestione */}
            <Button
              type="button"
              variant="text"
              icon="settings"
              aria-expanded={showStageSettings}
              onClick={() => setShowStageSettings(v => !v)}
            >
              Gestione
            </Button>

            {/* Help */}
            <IconButton icon="help_outline" aria-label="Aiuto" />
          </>
        }
      />

      <div className="kanban-toolbar">
        {/* Type toggle */}
        <SegmentedButton
          aria-label="Tipo di elemento"
          options={[
            { value: 'contract', label: 'Contratti', icon: 'description' },
            { value: 'lead', label: 'Lead', icon: 'person_search' },
          ]}
          value={entityType}
          onChange={v => setEntityType(v === 'lead' ? 'lead' : 'contract')}
        />

        {/* Pipeline selector pills */}
        <div className="kanban-pipelines">
          {pipelines.map(p => (
            <Chip
              key={p.id}
              variant="filter"
              selected={p.id === selectedPipeline}
              onClick={() => setSelectedPipeline(p.id)}
            >
              {p.name}
            </Chip>
          ))}
          {/* F-096: clona la pipeline selezionata (con le fasi). La copia nasce inattiva: non e' nell'elenco
              delle pipeline attive, quindi la aggiungiamo qui con il suo dettaglio e la apriamo. */}
          {selectedPipeline !== null && (
            <CloneConfigurationButton
              entityLabel="pipeline"
              url={`/api/pipelines/${selectedPipeline}/clone`}
              defaultName={pipelines.find(p => p.id === selectedPipeline)?.name ?? 'Pipeline'}
              note="Si copiano le fasi, con nuovi id e lo stesso ordine. Non si copiano lead e contratti. La copia nasce inattiva e non predefinita."
              onCloned={async id => {
                const detail = await fetchPipeline(id)
                setPipelines(prev => prev.some(p => p.id === id)
                  ? prev
                  : [...prev, { id, name: `${detail.name} (inattiva)`, departmentId: detail.departmentId, isDefault: false, sortOrder: detail.sortOrder }])
                setSelectedPipeline(id)
              }}
            />
          )}
          {selectedPipeline !== null && (
            <ConfigVersionHistoryButton entityType="Pipeline" entityId={selectedPipeline}
              label={pipelines.find(p => p.id === selectedPipeline)?.name ?? 'Pipeline'} />
          )}
          {selectedPipeline !== null && pipelineDetail?.id === selectedPipeline && pipelineDetail.isActive !== false && (
            <Button
              type="button"
              variant="text"
              size="sm"
              disabled={deactivating}
              onClick={() => void handleDeactivate(false)}
            >
              Disattiva
            </Button>
          )}
          {deactivateError && <span className="kanban-error">{deactivateError}</span>}
          {usageDialog && (
            <UsageConfirmDialog
              title="Disattivare il flusso?"
              message="Il flusso e' in uso: lead e pratiche gia' collegati restano collegati."
              usages={usageDialog}
              confirmLabel="Disattiva comunque"
              busy={deactivating}
              onConfirm={() => void handleDeactivate(true)}
              onCancel={() => setUsageDialog(null)}
            />
          )}
        </div>

        {/* Stats badges — TASK-0041: real totals across the selected pipeline's cards */}
        <StatusPill tone="primary">{totalCount} card</StatusPill>
        <StatusPill tone="info">{formatCurrency(totalValue)}</StatusPill>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <FilterBar onSubmit={handleFilterApply} onReset={handleFilterReset}>
          <TextField label="Dal" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          <TextField label="Al" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          <Select label="Reparto" value={department} onChange={e => setDepartment(e.target.value)}>
            <option value="">Tutti</option>
            {departments.map(d => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </Select>
          {/* TASK-0067: Agente shown for BOTH entity types — Lead has a real analogous field
              (AssignedAgentId, verified in Lead.cs), populated on real data by the legacy
              migration exactly like Contract.AgentId is. Previously this whole control (along
              with Operatore) was gated to entityType === 'contract' only, which was correct for
              Operatore (see below) but wrong for Agente. */}
          <Select label="Agente" value={agent} onChange={e => setAgent(e.target.value)}>
            <option value="">Tutti</option>
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </Select>
          {/* TASK-0067: Operatore stays contract-only — Lead has NO analogous field anywhere in
              its domain (verified by reading Lead.cs in full: no OperatorId, no equivalent
              concept under any name). */}
          {entityType === 'contract' && (
            <Select label="Operatore" value={operator} onChange={e => setOperator(e.target.value)}>
              <option value="">Tutti</option>
              {operators.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </Select>
          )}
        </FilterBar>
      )}

      {/* Error */}
      {error && <Callout tone="error">{error}</Callout>}

      {/* Move error — TASK-0041 */}
      {moveError && <Callout tone="error">Spostamento non riuscito: {moveError}</Callout>}

      {/* Loading */}
      {loading && <LoadingState label="Caricamento..." />}

      {/* Empty state */}
      {!loading && (pipelines.length === 0 || !pipelineDetail) && (
        <EmptyState
          icon="view_kanban"
          title="Nessuna pipeline"
          description="Seleziona o crea una pipeline per visualizzare la board."
          action={<Button type="button" variant="outlined">Crea pipeline</Button>}
        />
      )}

      {/* F-129: scelta della fase iniziale */}
      {showStageSettings && pipelineDetail && (
        <Card variant="outlined">
          <strong>Fase iniziale</strong>
          <p className="kanban-stage-settings__hint">
            È la fase in cui entrano le nuove voci di questa pipeline. Ce n&apos;è una sola; le fasi finali non si possono scegliere.
          </p>
          {stageError && <Callout tone="error">{stageError}</Callout>}
          <div role="radiogroup" aria-label="Fase iniziale" className="kanban-stage-settings__list">
            {sortedStages.map(stage => (
              <div
                key={stage.id}
                className={stage.isFinal ? 'kanban-stage-settings__row kanban-stage-settings__row--final' : 'kanban-stage-settings__row'}
              >
                <Radio
                  name="initial-stage"
                  label={stage.name}
                  checked={stage.isDefault}
                  disabled={stage.isFinal || savingStageId !== null}
                  onChange={() => void handleSetInitialStage(stage)}
                />
                {stage.isDefault && <InitialStageBadge />}
                {stage.isFinal && <span className="kanban-stage-settings__final">(finale)</span>}
              </div>
            ))}
          </div>
          {!sortedStages.some(s => s.isDefault) && (
            <Callout tone="error">Nessuna fase iniziale scelta: selezionane una.</Callout>
          )}
        </Card>
      )}

      {/* Kanban board */}
      {!loading && pipelineDetail && sortedStages.length > 0 && (
        <div className="kanban-board">
          {sortedStages.map(stage => (
            <KanbanColumn
              key={stage.id}
              stage={stage}
              cards={cardsByStage.get(stage.id) ?? []}
              cardsLoading={cardsLoading}
              movingId={movingId}
              onDropCard={cardId => {
                const card = cards.find(c => c.id === cardId)
                if (card) void handleMoveCard(card, stage)
              }}
            />
          ))}
        </div>
      )}
    </Page>
  )
}

/** F-129: badge sulla fase scelta come iniziale. */
function InitialStageBadge() {
  return <StatusPill tone="primary">Iniziale</StatusPill>
}

/** data-transfer MIME type used by the native HTML5 drag-and-drop below — a plain card id. */
const DRAG_MIME = 'application/x-equalcrm-kanban-card-id'

function KanbanColumn({
  stage,
  cards,
  cardsLoading,
  movingId,
  onDropCard,
}: {
  stage: PipelineStage
  cards: BoardCard[]
  cardsLoading: boolean
  movingId: number | null
  onDropCard: (cardId: number) => void
}) {
  const [dragOver, setDragOver] = useState(false)
  // TASK-0041 fix: PipelineStage really carries isFinal/finalStatus, not isClosedWon/isClosedLost
  // — see the remarks on PipelineStage in api/pipelines.ts.
  const stageIcon = stage.finalStatus === 'won'
    ? 'emoji_events'
    : stage.finalStatus === 'lost'
      ? 'block'
      : null
  const stageValue = cards.reduce((sum, c) => sum + c.amount, 0)

  return (
    // Il colore della fase arriva dai dati: unico valore dinamico, passato come variabile CSS.
    <div className="kanban-column" style={{ '--stage-color': stage.color } as CSSProperties}>
      {/* Column header */}
      <div className="kanban-column__header">
        {/* Left: icon/dot + name */}
        <div className="kanban-column__title">
          {stageIcon ? (
            <Icon name={stageIcon} size={18} className="kanban-column__icon" />
          ) : (
            <span className="kanban-column__dot" />
          )}
          <span className="kanban-column__name">{stage.name}</span>
          {stage.isDefault && <InitialStageBadge />}
        </div>

        {/* Right: count badge + value — TASK-0041: real per-column totals */}
        <div className="kanban-column__totals">
          <span className="kanban-column__count">{cards.length}</span>
          <span className="kanban-column__value">{formatCurrency(stageValue)}</span>
        </div>
      </div>

      {/* Drop zone — TASK-0041: native HTML5 drag-and-drop onto this zone calls onDropCard, which
          triggers the real PATCH .../pipeline move (see handleMoveCard above). Chosen over a
          "Sposta in..." menu because the "Trascina qui" copy/drag_indicator icon were already
          built into this markup, signalling that was the original intent — implementing it is a
          handful of drag/drop event handlers, not disproportionate for this task. */}
      <div
        className={dragOver ? 'kanban-column__body kanban-column__body--over' : 'kanban-column__body'}
        onDragOver={e => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault()
          setDragOver(false)
          const raw = e.dataTransfer.getData(DRAG_MIME)
          const cardId = Number(raw)
          if (raw && !Number.isNaN(cardId)) onDropCard(cardId)
        }}
      >
        {cards.map(card => (
          <KanbanCard
            key={card.id}
            card={card}
            moving={movingId === card.id}
          />
        ))}

        {/* Empty placeholder */}
        {!cardsLoading && cards.length === 0 && (
          <div className="kanban-column__placeholder">
            <Icon name="drag_indicator" size={24} />
            <p>Trascina qui</p>
          </div>
        )}
      </div>
    </div>
  )
}

function KanbanCard({ card, moving }: { card: BoardCard; moving: boolean }) {
  const [dragging, setDragging] = useState(false)
  const navigate = useNavigate()
  const cls = ['kanban-card', dragging && 'kanban-card--dragging', moving && 'kanban-card--moving'].filter(Boolean).join(' ')
  return (
    <Card
      variant="filled"
      padding="sm"
      as="div"
      className={cls}
      draggable
      onDragStart={e => {
        e.dataTransfer.setData(DRAG_MIME, String(card.id))
        e.dataTransfer.effectAllowed = 'move'
        setDragging(true)
      }}
      onDragEnd={() => setDragging(false)}
      onClick={() => navigate(card.href)}
      style={{ cursor: 'pointer' }}
      data-testid="kanban-card"
      data-card-id={card.id}
    >
      <span className="kanban-card__title">{card.title}</span>
      {card.subtitle && <span className="kanban-card__subtitle">{card.subtitle}</span>}
      <div className="kanban-card__meta">
        <span className="kanban-card__status">{card.statusLabel}</span>
        <span className="kanban-card__amount">{formatCurrency(card.amount)}</span>
      </div>
    </Card>
  )
}
